import React, {useState, useMemo} from 'react'
import {Button, Badge} from 'react-bootstrap'
import DataTable, {
  Column,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/table'
import {GridSortModel} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/pagination.types'
import {useGetInvoicesQuery} from '../../../../../services/cellcast.v2'
import {Invoice} from '../../../../../services/cellcast.v2.types'

interface InvoiceDisplayData {
  id: string
  date: string
  description: string
  totalQty: string
  amount: string
  status: 'Paid' | 'Pending' | 'Failed' | 'Draft'
  onDownload: () => void
  invoice: Invoice
}

const InvoicesReceipts: React.FC = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sortData, setSortData] = useState<GridSortModel>({field: 'date', sort: 'desc'})

  const [cursor, setCursor] = useState<{starting_after?: string; ending_before?: string}>({})
  const [cursorHistory, setCursorHistory] = useState<
    Array<{starting_after?: string; ending_before?: string}>
  >([{}])
  const limit = pageSize
  const {
    data: invoicesResponse,
    isLoading,
    error,
  } = useGetInvoicesQuery({
    limit,
    ...cursor,
  })

  const invoiceData: InvoiceDisplayData[] = useMemo(() => {
    const list =
      invoicesResponse?.data?.list || invoicesResponse?.data || (invoicesResponse as any)?.data
    const items: Invoice[] = list?.data || []

    return items.map((invoice: Invoice) => {
      const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-AU', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      }

      const formatAmount = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: currency.toUpperCase(),
        }).format(amount / 100)
      }

      const getStatus = (invoice: Invoice): 'Paid' | 'Pending' | 'Failed' | 'Draft' => {
        if (invoice.paid) return 'Paid'
        if (invoice.status === 'draft') return 'Draft'
        if (invoice.attempted && !invoice.paid) return 'Failed'
        return 'Pending'
      }

      const getDescription = (invoice: Invoice) => {
        if (invoice.lines.data.length > 0) {
          return invoice.lines.data[0].description
        }
        return 'Subscription'
      }

      const getTotalQty = (invoice: Invoice) => {
        if (invoice.lines.data.length > 0) {
          return invoice.lines.data[0].quantity.toString()
        }
        return '1'
      }

      return {
        id: invoice.id,
        date: formatDate(invoice.created),
        description: getDescription(invoice),
        totalQty: getTotalQty(invoice),
        amount: formatAmount(invoice.total, invoice.currency),
        status: getStatus(invoice),
        onDownload: () => {
          if (invoice.invoice_pdf) {
            window.open(invoice.invoice_pdf, '_blank')
          } else if (invoice.hosted_invoice_url) {
            window.open(invoice.hosted_invoice_url, '_blank')
          } else {
            console.log('No PDF available for invoice:', invoice.id)
          }
        },
        invoice,
      }
    })
  }, [invoicesResponse])

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'success'
      case 'Pending':
        return 'warning'
      case 'Failed':
        return 'danger'
      case 'Draft':
        return 'info'
      default:
        return 'secondary'
    }
  }

  const handleSortChange = (sortModel: GridSortModel) => {
    setSortData(sortModel)
  }

  const invoiceColumns: Column<InvoiceDisplayData>[] = [
    {
      id: 'date',
      label: 'DATE',
      width: 90,
      sortable: true,
      Cell: (row) => <span>{row.date}</span>,
    },
    {
      id: 'description',
      label: 'DESCRIPTION',
      width: 140,
      Cell: (row) => <span>{row.description}</span>,
    },
    {
      id: 'totalQty',
      label: 'TOTAL QTY',
      width: 80,
      Cell: (row) => <span>{row.totalQty}</span>,
    },
    {
      id: 'amount',
      label: 'AMOUNT',
      width: 90,
      Cell: (row) => <span className='fw-bold'>{row.amount}</span>,
    },
    {
      id: 'status',
      label: 'STATUS',
      width: 70,
      Cell: (row) => (
        <Badge bg={getStatusBadgeClass(row.status)} style={{fontSize: 13}}>
          {row.status}
        </Badge>
      ),
    },
    {
      id: 'actions',
      label: 'PDF',
      width: 70,
      Cell: (row) => (
        <Button
          variant='link'
          className='p-0 text-decoration-none fw-semibold'
          onClick={row.onDownload}
          style={{color: '#7c3aed'}}
        >
          Download
        </Button>
      ),
    },
  ]

  const totalData = invoiceData.length
  const instanceRows = useMemo(() => invoiceData, [invoiceData])

  const handleNext = () => {
    const list = invoicesResponse?.data?.list || (invoicesResponse as any)?.data
    const items: Invoice[] = list?.data || []
    const last = items[items.length - 1]
    if (last?.id) {
      const newCursor = {starting_after: last.id, ending_before: undefined}
      setCursor(newCursor)
      setCursorHistory((prev) => [...prev, newCursor])
      setPage((p) => p + 1)
    }
  }

  const handlePrev = () => {
    if (page > 0) {
      // Go back to previous cursor in history
      const newCursor = cursorHistory[page - 1]
      setCursor(newCursor)
      setCursorHistory((prev) => prev.slice(0, page))
      setPage((p) => Math.max(0, p - 1))
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 pt-5'
        style={{
          display: 'block',
        }}
      >
        <div className='card-title align-items-start flex-column'>
          <div className='d-flex justify-content-between align-items-center w-100'>
            <div>
              <span className='card-label fw-bold fs-3 mb-1'>INVOICES & RECEIPTS</span>
              <span className='text-muted mt-1 fw-semibold fs-7 d-block'>
                View your transaction history and download receipts
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='card-body p-0 pt-1'>
        {error ? (
          <div className='p-4 text-center'>
            <div className='text-danger'>
              <i className='bi bi-exclamation-triangle fs-1'></i>
              <p className='mt-2'>Failed to load invoices. Please try again later.</p>
            </div>
          </div>
        ) : (
          <DataTable
            items={instanceRows}
            isLoading={isLoading}
            columns={invoiceColumns}
            sortData={sortData}
            handleSortedChange={handleSortChange}
            pageSize={pageSize}
            totalData={totalData}
            handleChangePage={(newPage) => {
              if (newPage > page) handleNext()
              else if (newPage < page) handlePrev()
            }}
            handleChangSizeData={(newSize) => {
              setPageSize(newSize)
              setPage(0)
              setCursor({})
              setCursorHistory([{}])
            }}
            page={page}
            paginationType='cursor'
            hasNext={Boolean(invoicesResponse?.data?.list?.has_more)}
            hasPrev={page > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            onRowClick={(item) => console.log('Row clicked:', item)}
            onRowHover={(item, isHovering) => {}}
          />
        )}
      </div>
    </div>
  )
}

export default InvoicesReceipts
